import { deleteApBill, patchApBill, PublishApBillParams, PublishApBillResponse } from '@ar/network/AccountsReceivable.network';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@notch-ordering/ui-components';
import CheckIcon from '@icons/check-icon.svg';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ARRoutePaths } from '@v2/constants/EPaths';
import { AxiosError } from 'axios';
import { AP_BILL_QUERY_KEY, FETCH_AP_BILLS_QUERY_KEY } from '../queries/ApBillsQueries.hook';
import { queryClient } from '@/containers/app/Root';
// import { FETCH_TRANSACTION_QUERY_KEY } from '../queries/BillsQueries.hook';

export const useApBillUpdateMutation = () => useMutation(patchApBill, {
    onSuccess: async () => {
        await queryClient.invalidateQueries([AP_BILL_QUERY_KEY]);
    }
});

interface UseApBillMutationParams {
    mutationFn: (params:PublishApBillParams) => Promise<PublishApBillResponse>,
    successMessage: string,
    onSuccess?: (data: PublishApBillResponse) => void,
}

const requiredFieldsLabels = {
    externalId: 'External ID',
    billNumber: 'Bill Number',
    billDate: 'Bill Date',
    dueDate: 'Due Date',
    total: 'Total',
    terms: 'Terms',
    customerId: 'Customer',
};

type PublishError = AxiosError<{ error?: string, missing_fields:string[] }>;
const getMissingFieldsMessage = (missingFields: string[]) => missingFields.map((field) => requiredFieldsLabels[field]).join(', ');

const getErrorMessage = (error: PublishError): string => {
    const defaultMessage = 'Failed to publish bill';
    const response = error.response?.data;

    if (!response) {
        return defaultMessage;
    }

    if (response.missing_fields?.length) {
        return `There are unsaved missing fields: ${getMissingFieldsMessage(response.missing_fields)}`;
    }

    return response.error ?? defaultMessage;
};

export const usePublishOrRepublishApBillMutation = ({ mutationFn, successMessage, onSuccess }: UseApBillMutationParams) => {
    const navigate = useNavigate();
    return useMutation(mutationFn, {
        onSuccess: async (data) => {
            await queryClient.invalidateQueries([AP_BILL_QUERY_KEY]);
            // await queryClient.invalidateQueries([FETCH_TRANSACTION_QUERY_KEY]);

            toast.show({
                message: successMessage,
                icon: <CheckIcon />,
            });

            if (onSuccess) {
                onSuccess(data);
            }
            navigate(`/${ARRoutePaths.AR_BILLS}/${data.id}`);
        },
        onError: (error:PublishError) => {
            const message = getErrorMessage(error);
            toast.show({
                message
            });
        }
    });
};

export const useApBillDeleteMutation = () => useMutation(deleteApBill, {
    onSuccess: async () => {
        await queryClient.invalidateQueries([AP_BILL_QUERY_KEY]);
        await queryClient.invalidateQueries([FETCH_AP_BILLS_QUERY_KEY]);
    }
});
